import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import MaterialTable from 'material-table';
import { Paper, Tooltip, IconButton, Typography, Fab } from '@material-ui/core';
import { Add, Refresh, Delete } from '@material-ui/icons';
import { hasPermission, hasAtLeastOnePermission, BackButton, localization, getDimensionsOfPagination } from '../../sc-utilities';
import SupportedClientTypeDelete from './SupportedClientTypeDelete';
import * as actions from './store/supportedClientTypeActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import { CancelToken } from 'axios';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        }
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const SupportedClientTypeList = props => {
    const { classes, onFetchSupportedClientTypes, onFetchSupportedClientTypesCount, tableColumns, fetching, onBulkDeleteSupportedClientTypes, redirectHandler, saveColumnsFilter, activeCustomerID, translations, permissions, deleting, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, supportedClientTypeIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['supported_client_type_delete']),
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        exportFileName: 'Supported client types',
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchSupportedClientTypesCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count.data ? await onFetchSupportedClientTypes(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }


    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography>
                    {translations['supported_client_types'] || 'Supported client types'}
                </Typography>
                {
                    hasPermission(permissions, 'supported_client_type_create') ?
                        <Fab
                            id="create_supported_client_type"
                            variant="extended"
                            size="small"
                            color="primary"
                            aria-label="Add"
                            style={{ borderRadius: '1px' }}
                            className={classes.fab}
                            onClick={() => redirectHandler(`/admin/supported-client-type/create`)}
                        >
                            <Add size="small" />
                            {translations["create_new"] || "Create new"}
                        </Fab> : null
                }
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, supportedClientTypeIDs: [] });
    };

    const handleCloseDanger = async () => {
        await onBulkDeleteSupportedClientTypes({ SupportedClientTypeIDs: deleteModalState.supportedClientTypeIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, supportedClientTypeIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = supportedClientTypeIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, supportedClientTypeIDs: supportedClientTypeIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["supported_client_types"] || "Supported client types"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(supportedClientType => supportedClientType.SupportedClientTypeID))
        }
    ];

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`SupportedClientType`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar
                            data={props}
                            actions={tableActions}
                            selectedRows={props.selectedRows}
                            translations={translations}
                        />
                    )
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                onChangeRowsPerPage={(size) => {
                    setTableOptions({ ...tableOptions, pageSize: size })
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {
                hasPermission(permissions, 'supported_client_type_delete') ? (
                    <SupportedClientTypeDelete
                        entityName={translations['supported_client_type'] || 'Supported client type'}
                        translations={translations}
                        handleCloseDanger={handleCloseDanger}
                        handleCloseSafe={handleCloseSafe}
                        deleteModalState={deleteModalState}
                        deleting={deleting}
                    />
                ) : null
            }
        </Paper>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        deleting: state.admin.supportedClientType.deleting,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.admin.supportedClientType.fetching
    };
}

const mapDispatchToProps = dispatch => {
    return {
        onFetchSupportedClientTypesCount: (filterParams, options) => dispatch(actions.fetchSupportedClientTypeCount(filterParams, options)),
        onFetchSupportedClientTypes: (filterParams, options) => dispatch(actions.fetchSupportedClientType(filterParams, options)),
        onBulkDeleteSupportedClientTypes: supportedClientType => dispatch(actions.bulkDeleteSupportedClientTypes(supportedClientType)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(SupportedClientTypeList));